/*
 * ss-market-toggle.js — one control, two destinations, one submit.
 *
 * Dawn shipped the country picker and the language picker as two separate
 * forms. Flipping one left the other stale, which is how a Mexican visitor
 * landed on an English page with no way back. This element sends country and
 * language together and rebuilds the return path on the way out.
 *
 * Public surface, read by snippets/ss-market-toggle.liquid, by plan 02-03 and
 * by scripts/ss-toggle-path.test.mjs:
 *
 *   window.SsMarketToggle.targetPath({ currentRoot, targetRoot, pathname, search })
 *   window.SsMarketToggle.clearDismissals()
 *   window.SsMarketToggle.applyIntlCountry(root, value)
 *   window.SsMarketToggle.writeField(form, name, value)
 *
 * Borrowed from assets/localization-form.js by pattern, never by fork: the
 * Escape and Space handling, the arrow-key roving focus and the focus-out
 * close. Left behind on purpose: the country search, the diacritic filter and
 * the unguarded `.menu-drawer` lookups, which throw on any page without a
 * drawer.
 */

(function () {
  'use strict';

  var DISMISS_PREFIX = 'ss:locale-suggest:';
  var COUNTRY_PATTERN = /^[A-Za-z]{2}$/;

  /* ---------- the path builder (threat T-02-04) ---------- */

  function normalizeRoot(value) {
    if (typeof value !== 'string') return '';
    var root = value;
    if (root === '' || root === '/') return '';
    if (root.charAt(0) !== '/') return '';
    while (root.length > 1 && root.charAt(root.length - 1) === '/') {
      root = root.slice(0, root.length - 1);
    }
    return /^\/[A-Za-z0-9-]+$/.test(root) ? root : '';
  }

  function safeSearch(value) {
    if (typeof value !== 'string' || value === '') return '';
    if (value.charAt(0) !== '?') return '';
    if (value.indexOf('\\') > -1) return '';
    return value;
  }

  /**
   * Join a path to the destination locale root and refuse anything that is not
   * a same-origin path. `return_to` is the one value on this form a crafted
   * link can seed, so every rejection falls back to the destination root.
   */
  function targetPath(options) {
    var opts = options || {};
    var currentRoot = normalizeRoot(opts.currentRoot);
    var targetRoot = normalizeRoot(opts.targetRoot);
    var fallback = targetRoot + '/';

    var pathname = typeof opts.pathname === 'string' && opts.pathname !== '' ? opts.pathname : '/';

    if (currentRoot && (pathname === currentRoot || pathname.indexOf(currentRoot + '/') === 0)) {
      pathname = pathname.slice(currentRoot.length);
    }
    if (pathname === '') pathname = '/';
    if (pathname.charAt(0) !== '/') pathname = '/' + pathname;

    var joined = targetRoot + pathname;

    if (joined !== '/' && !/^\/[^/\\]/.test(joined)) return fallback;
    if (joined.indexOf('//') > -1) return fallback;
    if (joined.indexOf('\\') > -1) return fallback;
    if (joined.indexOf(':') > -1) return fallback;

    return joined + safeSearch(opts.search);
  }

  /* ---------- the dismissal reset ---------- */

  function clearDismissals() {
    try {
      var store = window.localStorage;
      if (!store) return;
      var doomed = [];
      for (var i = 0; i < store.length; i++) {
        var key = store.key(i);
        if (key && key.indexOf(DISMISS_PREFIX) === 0) doomed.push(key);
      }
      for (var j = 0; j < doomed.length; j++) {
        store.removeItem(doomed[j]);
      }
    } catch (error) {
      /* Private browsing denies localStorage. Restoring the visitor matters more. */
    }
  }

  /* ---------- the suggestion country (threat T-02-06) ---------- */

  function normalizeCountry(value) {
    if (typeof value !== 'string') return null;
    return COUNTRY_PATTERN.test(value) ? value.toUpperCase() : null;
  }

  /**
   * Shopify's localization form tag emits its own return_to input, so the form
   * holds two fields under that name. Writing only the first leaves a stale
   * value behind the one the endpoint reads. Write every match instead.
   * Returns the number of fields written.
   */
  function writeField(form, name, value) {
    if (!form || typeof form.querySelectorAll !== 'function') return 0;
    var fields = form.querySelectorAll('input[name="' + name + '"]');
    var written = 0;
    for (var i = 0; i < fields.length; i++) {
      fields[i].value = value;
      written += 1;
    }
    return written;
  }

  function applyIntlCountry(root, value) {
    var code = normalizeCountry(value);
    if (!code || !root || typeof root.querySelector !== 'function') return false;
    var button = root.querySelector('[data-ss-mode="INTL"]');
    if (!button) return false;
    button.setAttribute('data-country', code);
    return true;
  }

  window.SsMarketToggle = {
    targetPath: targetPath,
    clearDismissals: clearDismissals,
    normalizeCountry: normalizeCountry,
    applyIntlCountry: applyIntlCountry,
    writeField: writeField,
  };

  /* ---------- the element ---------- */

  if (customElements.get('ss-market-toggle')) return;

  customElements.define(
    'ss-market-toggle',
    class SsMarketToggleElement extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('.ss-toggle__button');
        this.panel = this.querySelector('.ss-toggle__list');
        this.options = Array.prototype.slice.call(this.querySelectorAll('.ss-toggle__option'));

        if (!this.form || !this.button || !this.panel) return;

        this.onKeyUp = this.onKeyUp.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onIntlCountry = this.onIntlCountry.bind(this);
        this.onOutsidePointer = this.onOutsidePointer.bind(this);

        this.addEventListener('keyup', this.onKeyUp);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('focusout', this.onFocusOut);
        this.button.addEventListener('click', this.toggle.bind(this));

        var self = this;
        this.options.forEach(function (option) {
          option.addEventListener('click', function (event) {
            self.choose(event.currentTarget);
          });
        });

        document.addEventListener('ss:intl-country', this.onIntlCountry);
        document.addEventListener('pointerdown', this.onOutsidePointer);
      }

      disconnectedCallback() {
        document.removeEventListener('ss:intl-country', this.onIntlCountry);
        document.removeEventListener('pointerdown', this.onOutsidePointer);
      }

      get expanded() {
        return this.button.getAttribute('aria-expanded') === 'true';
      }

      open() {
        this.panel.removeAttribute('hidden');
        this.button.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.panel.setAttribute('hidden', '');
        this.button.setAttribute('aria-expanded', 'false');
      }

      toggle() {
        if (this.expanded) {
          this.close();
        } else {
          this.open();
        }
      }

      onKeyUp(event) {
        var code = (event.code || '').toUpperCase();
        if (code === 'ESCAPE') {
          if (!this.expanded) return;
          event.stopPropagation();
          this.close();
          this.button.focus();
        } else if (code === 'SPACE' && event.target === this.button) {
          if (this.expanded) return;
          this.open();
        }
      }

      onKeyDown(event) {
        var code = (event.code || '').toUpperCase();
        if (code !== 'ARROWUP' && code !== 'ARROWDOWN') return;
        if (!this.options.length) return;
        event.preventDefault();
        if (!this.expanded) this.open();

        var index = this.options.indexOf(document.activeElement);
        var next;
        if (code === 'ARROWUP') {
          next = index > 0 ? index - 1 : this.options.length - 1;
        } else {
          next = index > -1 && index < this.options.length - 1 ? index + 1 : 0;
        }
        this.options[next].focus();
      }

      onFocusOut(event) {
        if (this.contains(event.relatedTarget)) return;
        this.close();
      }

      /*
       * focusout alone closes the panel on a desktop browser and leaves it open
       * on a phone: iOS Safari does not move focus on a tap outside a form
       * control, so nothing ever fires. pointerdown covers mouse, touch and pen
       * in one listener and fires before the tap has a chance to do anything
       * else. Added in 02-02c, when the control moved into the mobile header
       * and a tap outside it became the ordinary way to dismiss it.
       */
      onOutsidePointer(event) {
        if (!this.expanded) return;
        var target = event ? event.target : null;
        if (target && typeof this.contains === 'function' && this.contains(target)) return;
        this.close();
      }

      onIntlCountry(event) {
        var detail = event && event.detail ? event.detail : null;
        if (!detail) return;
        applyIntlCountry(this, detail.country);
      }

      choose(option) {
        if (!option) return;
        var form = this.form;
        var locale = option.getAttribute('data-locale') || '';
        var country = option.getAttribute('data-country') || '';

        if (option.getAttribute('data-ss-mode') === 'MX') clearDismissals();

        var path = targetPath({
          currentRoot: this.getAttribute('data-ss-root') || '',
          targetRoot: option.getAttribute('data-root') || '',
          pathname: window.location ? window.location.pathname : '',
          search: window.location ? window.location.search : '',
        });

        writeField(form, 'country_code', country);
        writeField(form, 'locale_code', locale);
        writeField(form, 'language_code', locale);
        writeField(form, 'return_to', path || option.getAttribute('data-path') || '/');

        // Luis, 2026-09-15: "cuando cambio de idioma se queda todo gris". The
        // drawer placement submits from inside Dawn's open menu, whose backdrop
        // and body scroll lock stay painted until the next document renders,
        // and iOS Safari's back-forward cache can restore them afterwards.
        // Close everything before navigating, and again if the page is
        // restored from cache.
        releaseOverlays();
        form.submit();
      }
    }
  );

  function releaseOverlays() {
    if (typeof document === 'undefined' || !document) return;
    var doc = document;
    var drawers = doc.querySelectorAll('details[open]');
    for (var i = 0; i < drawers.length; i++) drawers[i].removeAttribute('open');
    if (doc.body) doc.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
    if (doc.documentElement) doc.documentElement.classList.remove('overflow-hidden');
    var opened = doc.querySelectorAll('ss-market-toggle .ss-toggle__button[aria-expanded="true"]');
    for (var j = 0; j < opened.length; j++) opened[j].setAttribute('aria-expanded', 'false');
    var lists = doc.querySelectorAll('ss-market-toggle .ss-toggle__list');
    for (var k = 0; k < lists.length; k++) lists[k].hidden = true;
  }

  if (typeof window !== 'undefined' && window && typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', function (event) {
      if (event && event.persisted) releaseOverlays();
    });
  }
})();
